import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import Toast from '@showclix-shared/Toast';
import {
    ListingAppContext,
    Header,
    Footer,
    useTranslationService,
    translationService
} from '@showclix-shared/ListingComponents';
import ListingView from "./views/ListingView";
import { datesService } from "./services/DatesService";
import { getXssFilter } from '@showclix-shared-util/xssFilter';
import { AnalyticsContextProvider } from './contexts/Analytics';

export const AppContext = ListingAppContext;

export const DEFAULT_VIEW_GRID = "1";
export const DEFAULT_VIEW_CALENDAR = "2";

export const GRID_VIEW = 'grid-view';
export const CALENDAR_VIEW = 'calendar';

const defaultViewToTab = (defaultView) => {
    if (defaultView === DEFAULT_VIEW_CALENDAR) {
        return CALENDAR_VIEW;
    }
    return GRID_VIEW;
}

const iconToView = (icon) => {
    return icon === CALENDAR_VIEW ? 'calendar' : 'grid'
}

const App = (props) => {
    const date = (new URL(location.href))?.searchParams?.get("month")
    const [tab, setTab] = useState(date ? CALENDAR_VIEW : defaultViewToTab(props.defaultView));
    const [loadingListingId, setLoadingListingId] = useState(null);
    const [apiUrl, setApiUrl] = useState(props.configs.apiUrl);
    const [sellerShortName, setSellerShortName] = useState(props.sellerInfo.shortName);
    const [customTranslations, setCustomTranslations] = useState(props.configs.translations);
    const [customLocale, setCustomLocale] = useState(props.configs.jsLocale);

    // Helper to re-instantiate t with new translations
    const getTranslationServiceWithTranslations = (translations) => {
        return useTranslationService(translations || props.configs.translations);
    };

    // Use either custom translations or default
    const { t, isValidLocale } = getTranslationServiceWithTranslations(customTranslations);

    useEffect(() => {
      datesService().useLocale(customLocale);
    }, [customLocale])

    // show an error message if we detect that the embed encountered a rendering error
    const embedHasError = useRef(false);
    useEffect(() => {
        if (!embedHasError.current && props.embedCaughtRenderingError === true) {
            Toast.error(t('org_listing__an_error_occurred'));
            embedHasError.current = true;
        }
    }, [props.embedCaughtRenderingError]);

    // Locale change listener and translation updater
    useEffect(() => {
      const { getTranslationVersion, getTranslations } = translationService(apiUrl);
      const updateTranslation = async (serverLocale, jsLocale) => {
        try {
          const { version } = await getTranslationVersion(serverLocale, 'org_listing');
          const { locale_strings } = await getTranslations(serverLocale, 'org_listing', version);
          setCustomLocale(jsLocale);
          setCustomTranslations(locale_strings || {});
        } catch (error) {
          console.error(error);
        }
      };
      const handler = async (e) => {
        if (e && e.detail) {
          await updateTranslation(e.detail.serverLocale, e.detail.jsLocale);
        }
      };
      window.addEventListener('localeChanged', handler);
      return () => {
        window.removeEventListener('localeChanged', handler);
      };
    }, [apiUrl]);
    const appContextData = () => ({
      apiUrl,
      setApiUrl,
      sellerShortName,
      setSellerShortName,
      updateOrgListingConfig: props.updateOrgListingConfig,
      t,
      locale: isValidLocale(customLocale) ? customLocale : 'en-US',
      serverLocale: props.configs.locale,
      serverTimezoneName: props.configs.serverTimezoneName,
      hideStartingPrices: props.configs.hideStartingPrices,
      embeddedCheckout: props.embeddedCheckout,
      loadingListingId,
      setLoadingListingId,
      setCustomTranslations,
      setCustomLocale,
      tab,
      setTab,
      settings: props.settings,
      membershipConfig: props.membershipConfig,
      embedCaughtRenderingError: props.embedCaughtRenderingError,
      hideSearchAndDisplayControls: props.configs.hideSearchAndDisplayControls,
      orgListingConfig: {
        accentColor: props.configs?.accentColor || null,
        queueIt: props.configs?.queueIt || null,
        queueOn: props.configs?.queueOn || null,
        slug: props.sellerInfo?.shortName || null,
        isCartMode: !!props.embeddedCheckout,
        listings: props.listings || [],
        type: props.settings?.type,
        view: iconToView(tab),
      }
    });

    // find the requested listing document in the initial result set
    // if a listing was requested in the url for cart mode
    const requestedListingDocument = useMemo(() => {
        return props.embeddedCheckout && props.requestedListingId
            ? props.listings?.find(doc => doc.listing_id == props.requestedListingId)
            : null;
    });
    const messageDisplay = props.sellerInfo.sellerFooterMessage &&  (
      <>
        <div   dangerouslySetInnerHTML={{
          __html: getXssFilter().process(props.sellerInfo.sellerFooterMessage),
        }}>
          </div>
      </>
    );

    return (
      <AppContext.Provider value={appContextData()}>
        <AnalyticsContextProvider analytics={props.configs.analytics}>
          <Header logo={props.sellerInfo.partnerLogo} />
          <ListingView
            listings={props.listings ?? []}
            userPhoto={props.sellerInfo.userImage}
            organizationInfo={props.sellerInfo.organizationInfo}
            socialMedia={props.sellerInfo.social}
            backgroundImage={props.sellerInfo.backgroundImage}
            description={props.sellerInfo.description || props.settings.tag_line}
            isPro={props.sellerInfo.isPro}
            requestedListingDocument={requestedListingDocument}
            defaultView={props.settings.default_view}
            sellerId={props.sellerInfo.sellerId}
          />
          <Footer
            logo={props.sellerInfo.partnerLogo}
            copyrightName={props.sellerInfo.copyrightName ?? ''}
            contactUrl={props.sellerInfo?.contact_url ?? ''}
            tosUrl={props.sellerInfo?.tos_url ?? ''}
            privacyUrl={props.sellerInfo?.privacy_url ?? ''}
            doNotSellUrl={props.sellerInfo?.do_not_sell_url ?? ''}
            messages={messageDisplay}
            versionIdentifier={props?.configs?.versionIdentifier}
          />
          <Toast />
        </AnalyticsContextProvider>
      </AppContext.Provider>
    );
}

App.propTypes = {
    sellerInfo: PropTypes.object.isRequired,
    listings: PropTypes.array,
    configs: PropTypes.object.isRequired,
    embeddedCheckout: PropTypes.object,
    requestedListingId: PropTypes.number,
    embedCaughtRenderingError: PropTypes.bool,
    settings: PropTypes.object
}

export default App;
